import { Table, Tag, Space } from 'antd'
import type { ReactNode } from 'react'
import type { ColumnsType } from 'antd/es/table'
import type { SurveyResponseItem, SurveyQuestion, SurveyStepAnswer } from '../types/survey.types'
import './SurveyResponsesTable.css'

interface SurveyResponsesTableProps {
  responses: SurveyResponseItem[]
  questions: SurveyQuestion[]
  loading: boolean
  onRowClick: (response: SurveyResponseItem) => void
}

function renderAnswer(answer: SurveyStepAnswer | undefined, question: SurveyQuestion): ReactNode {
  if (!answer) return '-'

  if (question.type === 'multi-select') {
    const hasValues = answer.values && answer.values.length > 0
    if (!hasValues && !answer.otherText) return '-'
    return (
      <Space size={[4, 4]} wrap>
        {answer.values?.map(v => (
          <Tag key={v} style={{ fontSize: '11px', padding: '0 7px', margin: 0 }}>
            {v}
          </Tag>
        ))}
        {answer.otherText && (
          <Tag color="orange" style={{ fontSize: '11px', padding: '0 7px', margin: 0 }}>
            Others: {answer.otherText}
          </Tag>
        )}
      </Space>
    )
  }

  if (question.type === 'single-select') {
    return answer.value ? <Tag style={{ fontSize: '11px', padding: '0 7px', margin: 0 }}>{answer.value}</Tag> : '-'
  }

  if (question.type === 'text') {
    return answer.text || '-'
  }

  return '-'
}

export function SurveyResponsesTable({ responses, questions, loading, onRowClick }: SurveyResponsesTableProps) {
  const sortedQuestions = [...questions].sort((a, b) => a.step - b.step)

  const fixedColumns: ColumnsType<SurveyResponseItem> = [
    {
      title: 'Tenant',
      key: 'tenant',
      width: 140,
      sorter: (a, b) => (a.tenantName || a.tenantCode).localeCompare(b.tenantName || b.tenantCode),
      render: (_, record) => record.tenantName || record.tenantCode
    },
    {
      title: 'User',
      key: 'user',
      width: 180,
      sorter: (a, b) => (a.userEmail || a.userId).localeCompare(b.userEmail || b.userId),
      render: (_, record) => record.userEmail || record.userId
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, record) => {
        if (record.isCompleted) return <Tag color="success">Completed</Tag>
        if (record.isDismissed) return <Tag color="warning">Dismissed</Tag>
        if (record.currentStep > 0)
          return (
            <Tag color="processing">
              Step {record.currentStep}/{sortedQuestions.length}
            </Tag>
          )
        return <Tag>Not Started</Tag>
      },
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'In Progress', value: 'inProgress' },
        { text: 'Dismissed', value: 'dismissed' }
      ],
      onFilter: (value, record) => {
        if (value === 'completed') return record.isCompleted
        if (value === 'inProgress') return !record.isCompleted && !record.isDismissed && record.currentStep > 0
        if (value === 'dismissed') return record.isDismissed && !record.isCompleted
        return true
      }
    }
  ]

  const questionColumns: ColumnsType<SurveyResponseItem> = sortedQuestions.map(q => ({
    title: `Q${q.step}`,
    key: `q${q.step}`,
    width: q.type === 'multi-select' ? 250 : 180,
    render: (_: unknown, record: SurveyResponseItem) => {
      const answer = record.answers?.[String(q.step)]
      return renderAnswer(answer, q)
    }
  }))

  const trailingColumns: ColumnsType<SurveyResponseItem> = [
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 160,
      sorter: (a, b) => {
        if (!a.submittedAt) return 1
        if (!b.submittedAt) return -1
        return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
      },
      render: (value: string) => (value ? new Date(value).toLocaleString() : '-')
    }
  ]

  const columns = [...fixedColumns, ...questionColumns, ...trailingColumns]

  return (
    <div className="survey-responses-table-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
      <Table
        columns={columns}
        dataSource={responses}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: total => `${total} responses` }}
        onRow={record => ({
          onClick: () => onRowClick(record),
          style: { cursor: 'pointer' }
        })}
        style={{ flex: 1 }}
      />
    </div>
  )
}
